// Перечисления трёх видов (4.4): «-» (dash), «а)» (letter), «1)» (digit); вложенность до 3 уровней.
// kind = 'auto': верхний уровень - дефис, под буквенным - цифры (как writer/nodes_text.list_kind).
import { Node, mergeAttributes, type Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { idAttr } from './util';

export type ListKind = 'auto' | 'dash' | 'letter' | 'digit';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    stoList: {
      /** Сделать текущие абзацы перечислением вида kind (или сменить вид существующего). */
      setStoList: (kind: ListKind) => ReturnType;
      /** Снять перечисление. */
      unsetStoList: () => ReturnType;
    };
  }
}

export const StoListItem = Node.create({
  name: 'stoListItem',
  content: 'paragraph stoList?',
  defining: true,
  parseHTML() { return [{ tag: 'li' }]; },
  renderHTML({ HTMLAttributes }) { return ['li', HTMLAttributes, 0]; },
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.splitListItem(this.name),
      Tab: () => {
        // 4.4.4: перечисление и его детализация - не больше двух уровней
        const { $from } = this.editor.state.selection;
        let depth = 0;
        for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'stoList') depth++;
        if (depth >= 2 && $from.node(-1)?.type.name === this.name) {
          window.dispatchEvent(new CustomEvent('sto-toast', { detail: { text: 'Третий уровень перечисления СТО не предусматривает (4.4.4: перечисление и его детализация)', kind: 'info' } }));
          return true;
        }
        return this.editor.commands.sinkListItem(this.name);
      },
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
      Backspace: () => {
        const { $from, empty } = this.editor.state.selection;
        if (!empty || $from.parentOffset !== 0) return false;
        if ($from.node(-1)?.type.name !== this.name) return false;
        return this.editor.commands.liftListItem(this.name);
      },
    };
  },
});

export const StoList = Node.create({
  name: 'stoList',
  group: 'block',
  content: 'stoListItem+',
  addAttributes() {
    return {
      ...idAttr(),
      kind: {
        default: 'auto' as ListKind,
        parseHTML: (el) => {
          const k = el.getAttribute('data-kind');
          if (k) return k;
          if (el.tagName === 'OL') {
            const t = (el.getAttribute('type') || '').toLowerCase();
            return t === 'a' || t === 'а' ? 'letter' : 'digit';
          }
          return 'auto';
        },
        renderHTML: (a) => ({ 'data-kind': a.kind }),
      },
      // номер первой позиции (1 - «а)»/«1)»): перечисление-продолжение после рисунка/формулы/таблицы («в)» после
      // «б)» - шаги Word-нумерации, разорванные объектами; импорт .docx, «Продолжить нумерацию»); writer - nodes_text
      start: {
        default: 1,
        parseHTML: (el) => Math.max(1, Number(el.getAttribute('data-start') || el.getAttribute('start') || 1) || 1),
        renderHTML: (a) => (Number(a.start) > 1 ? { 'data-start': a.start, style: `counter-reset: sto-item ${Number(a.start) - 1}` } : {}),
      },
    };
  },
  parseHTML() { return [{ tag: 'ul' }, { tag: 'ol' }]; },
  renderHTML({ node, HTMLAttributes }) {
    const tag = node.attrs.kind === 'digit' || node.attrs.kind === 'letter' ? 'ol' : 'ul';
    return [tag, mergeAttributes(HTMLAttributes, { class: 'sto-list' }), 0];
  },
  addCommands() {
    return {
      setStoList: (kind) => ({ editor, chain }) => {
        const { $from } = editor.state.selection;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === this.name) {
            return chain().command(({ tr }) => {
              tr.setNodeMarkup($from.before(d), undefined, { ...$from.node(d).attrs, kind });
              return true;
            }).run();
          }
        }
        return chain().toggleList(this.name, 'stoListItem').updateAttributes(this.name, { kind }).run();
      },
      unsetStoList: () => ({ commands }) => commands.liftListItem('stoListItem'),
    };
  },
});

export const listExtensions = () => [StoList, StoListItem];

/** Перечисление верхнего уровня у курсора: {pos, node} или null. */
function topListAt(state: EditorState): { pos: number; node: PMNode } | null {
  const { $from } = state.selection;
  let found: { pos: number; node: PMNode } | null = null;
  for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'stoList') found = { pos: $from.before(d), node: $from.node(d) };
  return found;
}

/** «Продолжить нумерацию»: перечисление у курсора продолжает предыдущее перечисление того же вида (после рисунка,
 *  формулы, таблицы, абзаца-пояснения); restart - начать заново с «а)»/«1)». -> false, если продолжать нечего. */
export function setListNumbering(editor: Editor, restart = false): boolean {
  const { state } = editor;
  const cur = topListAt(state);
  if (!cur) return false;
  let start = 1;
  if (!restart) {
    let prev: { node: PMNode } | null = null;
    const $p = state.doc.resolve(cur.pos);
    const parent = $p.parent;
    for (let i = $p.index() - 1; i >= 0; i--) {
      const n = parent.child(i);
      if (n.type.name === 'stoList') { prev = { node: n }; break; }
      if (n.type.name === 'heading' || n.type.name === 'structHeading') break;     // в другом разделе - не продолжение
    }
    if (!prev) return false;
    start = Math.max(1, Number(prev.node.attrs.start) || 1) + prev.node.childCount;
  }
  editor.view.dispatch(state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, start }));
  return true;
}
